import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";

import { Input, Label, Button, MoneyInput } from "@/components/ui";
import { useCreateProcedureMutation, useToast } from "@/hooks";
import { useStockItems } from "@/hooks/queries/inventory/useStockItems";
import { ProceduresValidationSchema } from "@/shared";
import { DisplayError } from "@/utils";
import { ImportProcedureModal } from "./import-procedure-modal";
import { ProcedureConsumablesFields } from "./procedure-consumables-fields";

type FormCreateProcedureProps = {
  handleClose: () => void;
};
export function FormCreateProcedure({ handleClose }: FormCreateProcedureProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  const { mutateAsync, isLoading } = useCreateProcedureMutation();
  const { data: stockItems = [], isLoading: isLoadingStockItems } =
    useStockItems();

  const initialValues = {
    name: "",
    value: "",
    stock_items: [],
    // time: "",
    // exam: "",
    // covenant: "",
  };

  const { handleSubmit, ...formik } = useFormik({
    initialValues,
    validationSchema: ProceduresValidationSchema(),
    onSubmit: async (values) => {
      handleCreateProcedureSubmit(values);
    },
  });

  async function handleCreateProcedureSubmit(
    request: Parameters<typeof mutateAsync>[0],
  ) {
    try {
      await mutateAsync(request, {
        onSuccess: () => {
          handleClose();
          formik.resetForm();
          toast({
            title: t("LABEL.success"),
            description: t("MESSAGE.created successfully"),
          });
        },
        onError: (error) => {
          toast({
            title: t("LABEL.error"),
            description: DisplayError(error, t("MESSAGE.error_message"), t),
            variant: "destructive",
          });
        },
      });
    } catch (error) {
      toast({
        title: t("LABEL.error"),
        description: DisplayError(error, t("MESSAGE.error_message"), t),
        variant: "destructive",
      });
    } finally {
      formik.setSubmitting(false);
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="max-h-[70vh] 2xl:max-h-[90vh] flex flex-col gap-4"
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-zinc-700">Novo Procedimento</h2>
        <Button
          type="button"
          variant="outline"
          onClick={() => setIsImportModalOpen(true)}
          className="text-sm"
        >
          Importar CSV
        </Button>
      </div>
      <div className="h-full space-y-2">
        <div className="grid grid-cols-1 gap-2">
          <div className="space-y-1">
            <Label required>{t("LABEL.name")}</Label>
            <Input
              isInvalid={formik.touched.name && formik.errors.name}
              isValid={formik.touched.name && !formik.errors.name}
              {...formik.getFieldProps("name")}
              messageError={formik.errors.name}
            />
          </div>
          <div className="space-y-1">
            <Label required>{t("LABEL.value")}</Label>
            <MoneyInput
              isInvalid={formik.touched.value && formik.errors.value}
              isValid={formik.touched.value && !formik.errors.value}
              {...formik.getFieldProps("value")}
              messageError={formik.errors.value}
            />
          </div>
          <ProcedureConsumablesFields
            items={stockItems}
            value={formik.values.stock_items}
            onChange={(stock_items) =>
              formik.setFieldValue("stock_items", stock_items)
            }
            isLoading={isLoadingStockItems}
          />
          {/* <div className="space-y-1">
            <Label required>
              {t("LABEL.time")}/{t("LABEL.minutes")}
            </Label>
            <Input
              isInvalid={formik.touched.time && formik.errors.time}
              isValid={formik.touched.time && !formik.errors.time}
              {...formik.getFieldProps("time")}
              messageError={formik.errors.time}
            />
          </div> */}
          {/* <div className="space-y-1">
            <Label required>{t("LABEL.exam")}</Label>

            <div className="space-y-1">
              <Select
                {...formik.getFieldProps("exam")}
                onValueChange={(value) => {
                  formik.setFieldTouched("exam", true);
                  formik.setFieldValue("exam", value);
                }}
                defaultValue={initialValues.exam}
              >
                <SelectTrigger
                  isInvalid={formik.touched.exam && formik.errors.exam}
                  isValid={formik.touched.exam && !formik.errors.exam}
                  className="disabled:opacity-100"
                >
                  <SelectValue placeholder="Sim ou não" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="sim">{t("LABEL.yes")}</SelectItem>
                    <SelectItem value="não">{t("LABEL.no")}</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
              <MessageError
                isInvalid={formik.touched.exam && formik.errors.exam}
                messageError={formik.errors.exam}
              />
            </div>
          </div> */}
        </div>
        {/* <div className="grid grid-cols-1 gap-2">
          <div className="space-y-1">
            <Label required>{t("LABEL.agreement")}</Label>

            <div className="space-y-1">
              <Select
                {...formik.getFieldProps("covenant")}
                onValueChange={(value) => {
                  formik.setFieldTouched("covenant", true);
                  formik.setFieldValue("covenant", value);
                }}
                defaultValue={initialValues.covenant}
              >
                <SelectTrigger
                  isInvalid={formik.touched.covenant && formik.errors.covenant}
                  isValid={formik.touched.covenant && !formik.errors.covenant}
                  className="disabled:opacity-100"
                >
                  <SelectValue placeholder="Sim ou não" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="yes">{t("LABEL.yes")}</SelectItem>
                    <SelectItem value="no">{t("LABEL.no")}</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
              <MessageError
                isInvalid={formik.touched.covenant && formik.errors.covenant}
                messageError={formik.errors.covenant}
              />
            </div>
          </div>
        </div> */}
      </div>
      <div className="flex justify-center items-center gap-2">
        <Button isLoading={isLoading} disabled={isLoading} className="px-10">
          {t("LABEL.save")}
        </Button>
      </div>
      
      <ImportProcedureModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onSuccess={handleClose}
      />
    </form>
  );
}
